import { getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { messaging, VAPID_KEY } from '../firebase/config';

export type NotificationHandler = (payload: MessagePayload) => void;

/**
 * Request notification permission and get FCM token
 * @returns FCM token if permission granted, null otherwise
 */
export const requestNotificationPermission = async (): Promise<string | null> => {
  if (!messaging) {
    return null;
  }

  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      return null;
    }

    // Request permission
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        return token;
      } else {
        return null;
      }
    } else if (permission === 'denied') {
      return null;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

/**
 * Set up foreground message listener
 * @param handler Callback function to handle incoming messages
 */
export const onForegroundMessage = (handler: NotificationHandler): void => {
  if (!messaging) {
    return;
  }

  onMessage(messaging, (payload) => {
    handler(payload);

    // Show browser notification for foreground messages
    if (payload.notification) {
      const { title, body } = payload.notification;

      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification(title || 'New Notification', {
          body: body || 'You have a new notification',
          icon: '/logo192.png',
          badge: '/logo192.png',
          data: payload.data,
        });

        notification.onclick = () => {
          window.focus();
          notification.close();

          // Handle navigation based on notification data
          if (payload.data?.url) {
            window.location.href = payload.data.url;
          }
        };
      }
    }
  });
};

/**
 * Send FCM token to backend
 * @param token FCM token
 * @param apiBaseUrl Backend API base URL
 */
export const sendTokenToBackend = async (
  token: string,
  apiBaseUrl: string = process.env.REACT_APP_API_URL || 'https://api.crc.it.com/api'
): Promise<boolean> => {
  try {
    const authTokensStr = localStorage.getItem('auth_tokens');
    if (!authTokensStr) {
      return false;
    }

    const authTokens = JSON.parse(authTokensStr);
    const accessToken = authTokens?.access;

    if (!accessToken) {
      return false;
    }

    const response = await fetch(`${apiBaseUrl}/notifications/register-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        fcm_token: token,
        device_type: 'web',
      }),
    });

    if (response.ok) {
      return true;
    } else {
      const errorData = await response.json();
      return false;
    }
  } catch (error) {
    return false;
  }
};

/**
 * Delete FCM token from backend (on logout)
 * @param apiBaseUrl Backend API base URL
 */
export const deleteTokenFromBackend = async (
  apiBaseUrl: string = process.env.REACT_APP_API_URL || 'https://api.crc.it.com/api'
): Promise<boolean> => {
  try {
    const authTokensStr = localStorage.getItem('auth_tokens');
    if (!authTokensStr) {
      return false;
    }

    const authTokens = JSON.parse(authTokensStr);
    const accessToken = authTokens?.access;

    if (!accessToken) {
      return false;
    }

    const response = await fetch(`${apiBaseUrl}/notifications/delete-token`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (response.ok) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};
